import { ProviderNotesTemplate } from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import { buildSingleItemPdfEntry } from './pdfOverride';

type ProviderNotesRecordsKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_RECORDS; type: DocumentType.PROVIDER_NOTES }
>;

export interface ProviderNotesRecordsQueryData {
  curated: {
    id: string;
    providerType?: { name: string; displayName: string };
    alternateTypeName?: string;
    providerName: string;
    date: string;
    comments?: string;
    createdAt: string | Date;
    images: Array<{ id: string; signedUrl?: string; createdAt: string | Date }>;
  };
}

export const adaptProviderNotesRecords = (
  data: ProviderNotesRecordsQueryData,
  key: ProviderNotesRecordsKey,
): DocumentEntry | undefined => {
  const curated = data?.curated;
  if (!curated) return undefined;

  const firstImageWithUrl = curated.images.find((image) => !!image.signedUrl);
  if (firstImageWithUrl?.signedUrl) {
    return {
      kind: 'pdf',
      signedUrl: firstImageWithUrl.signedUrl,
      title: key.name ?? curated.alternateTypeName,
      uploadedAt: firstImageWithUrl.createdAt ?? curated.createdAt,
    };
  }

  const pdfEntry = buildSingleItemPdfEntry(
    [curated],
    (item) =>
      item.images.map((image) => ({
        signedUrl: image.signedUrl,
        title: key.name ?? item.alternateTypeName,
        uploadedAt: image.createdAt ?? item.createdAt,
      })),
    { title: key.name, uploadedAt: curated.createdAt },
  );
  if (pdfEntry) return pdfEntry;

  return {
    kind: 'template',
    title: key.name,
    uploadedAt: curated.createdAt,
    render: (ctx) => (
      <ProviderNotesTemplate
        id={curated.id}
        providerType={curated.providerType?.displayName}
        alternateTypeName={curated.alternateTypeName}
        providerName={curated.providerName}
        date={curated.date}
        comments={curated.comments}
        images={curated.images}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
      />
    ),
  };
};
